/**
 * FMF Website - GTM Implementation
 * 
 * All GTM-related tracking logic is placed in this file.
 */

// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

/**
 * Utility function to sanitize tracking data according to requirements:
 * - All values to lowercase
 * - Spaces replaced with underscores
 * - Undefined values are handled (converted to empty string or omitted depending on usage)
 */
const transformObject = (obj) => {
    const newObj = {};
    for (let key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            // Do not pass undefined values as per requirements
            if (obj[key] !== undefined && obj[key] !== null) {
                // Ensure the value is converted to string, lowercased, and spaces replaced with underscores
                // Note: The PDF mentions 'page_title' can be in Arabic, so we won't strictly enforce English-only here via code, 
                // but we will ensure casing and underscore rules are applied.
                newObj[key] = obj[key].toString().toLowerCase().replace(/\s+/g, '_');
            }
        }
    }
    return newObj;
};

/**
 * Page View Tracking
 * Triggered on load of all pages.
 */
document.addEventListener('DOMContentLoaded', function() {
    // CMS developer can inject data-* attributes to the body tag for exact values
    const pageLanguage = document.body?.getAttribute('umb-lang') || document.documentElement.lang || 'en';
    const pageId = document.body?.getAttribute('data-body-id') || '';
    const pageName = document.body?.getAttribute('data-body-class') || document.title;
    
    // Push the Page View event payload (Template removed per instructions)
    window.dataLayer.push(transformObject({
        'page_id': pageId,
        'page_language': pageLanguage,
        'page_url': window.location.href,
        'page_title': document.title,
        'page_name': pageName
    }));
});

/**
 * Utility function to handle CTA clicks with navigation delay
 */
const handleCTAClick = (event, trackingData) => {
    window.dataLayer.push(transformObject(trackingData));
    
    const targetLink = event.target.closest('a');
    if (!targetLink) return;

    const url = targetLink.getAttribute('href');
    const isExternal = targetLink.hostname && targetLink.hostname !== window.location.hostname;

    // If it's a valid navigation link
    if (url && url !== '#' && !url.startsWith('javascript:')) {
        if (isExternal || targetLink.getAttribute('target') === '_blank') {
            // Open in new tab; no need to prevent default or delay
            return;
        } else {
            // Internal link: Prevent default navigation, wait 200ms for tracking, then navigate
            event.preventDefault();
            setTimeout(() => {
                window.location.href = url;
            }, 200);
        }
    }
};

// --- Event Listeners ---
// Specific event listeners for components will be added below.

document.addEventListener('click', function(event) {
    // 1. Homepage Hero Banner CTA
    const homeHeroCTA = event.target.closest('#home-hero-cta');
    if (homeHeroCTA) {
        const headingElement = document.getElementById('home-hero-heading');
        
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title, // 'page_name' usually refers to title, per reference.js implementation
            'click_cta_name': homeHeroCTA.textContent.trim(),
            'click_category': headingElement ? headingElement.textContent.trim() : '',
            'click_sub_category': 'hero_banner'
        });
        return;
    }

    // 2. Global Header CTAs
    // Tracks all links inside #mainNav EXCEPT those that open dropdowns (.dropdown-toggle)
    const headerCTA = event.target.closest('#mainNav a');
    if (headerCTA && !headerCTA.classList.contains('dropdown-toggle')) {
        let ctaName = headerCTA.textContent.trim();
        if (headerCTA.classList.contains('nav-logo') || headerCTA.closest('.nav-logo')) {
            ctaName = 'logo';
        }
        
        const ctaUrl = headerCTA.getAttribute('href') || '';
        const isDropdownLink = headerCTA.closest('.mega-menu') !== null;
        
        const payload = {
            'event': 'eventtracker',
            'custom_event_name': 'navigation',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': ctaName,
            'click_cta_url': ctaUrl,
            'click_category': 'global_header'
        };

        if (isDropdownLink) {
            payload['click_sub_category'] = 'nav_dropdown';
        }
        
        handleCTAClick(event, payload);
        return;
    }

    // 3. Global Footer CTAs
    // Tracks all links inside .footer-wrap
    const footerCTA = event.target.closest('.footer-wrap a');
    if (footerCTA) {
        let ctaName = footerCTA.textContent.trim();
        let subCategory = null;
        
        if (footerCTA.classList.contains('footer-logo')) {
            ctaName = 'footer-logo';
        } else if (footerCTA.classList.contains('footer-social-icon')) {
            ctaName = footerCTA.getAttribute('aria-label') || 'social_icon';
            subCategory = 'social';
        }
        
        const ctaUrl = footerCTA.getAttribute('href') || '';
        
        const payload = {
            'event': 'eventtracker',
            'custom_event_name': 'navigation',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': ctaName,
            'click_cta_url': ctaUrl,
            'click_category': 'global_footer'
        };

        if (subCategory) {
            payload['click_sub_category'] = subCategory;
        }
        
        handleCTAClick(event, payload);
        return;
    }

    // 4. Feature Blocks
    const featureCta = event.target.closest('.dialogue-section-wrap .cta-btn, .discover-section .cta-btn, .momentum-section .cta-btn, .berometer-quote-section .cta-btn, .berometer-quote-section .download-cta');
    if (featureCta) {
        const featureSection = featureCta.closest('.dialogue-section-wrap, .discover-section, .momentum-section, .berometer-quote-section');
        const headingElement = featureSection ? featureSection.querySelector('.section-title, .discover-title, .momentum-title, .quote-author') : null;
        
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': featureCta.textContent.trim() || 'click',
            'click_category': headingElement ? headingElement.textContent.trim() : '',
            'click_sub_category': 'feature_block'
        });
        return;
    }

    // 5. Card Carousel Sliders
    const carouselCta = event.target.closest('.home-pillars .cta-btn, .home-strategy .cta-btn, .about-strategy .cta-btn, .media-section .card-btn, .home-media .cta-btn, .home-media .media-card-content a, .initiatives-opportunities .opportunity-card, .home-media .media-card');
    if (carouselCta) {
        const carouselSection = carouselCta.closest('.home-pillars, .home-strategy, .about-strategy, .media-section, .home-media, .initiatives-opportunities');
        const headingElement = carouselSection ? carouselSection.querySelector('.section-title, .strategy-title, .media-title, .initiatives-outputs-title') : null;
        
        let index = null;
        let ctaName = carouselCta.textContent.trim() || 'explore';
        
        // If they clicked a card specifically, find its index
        const card = carouselCta.closest('.media-card, .opportunity-card');
        if (card) {
            const cards = Array.from(carouselSection.querySelectorAll('.media-card, .opportunity-card'));
            index = cards.indexOf(card) + 1;
            const cardTitle = card.querySelector('.media-card-title, .opportunity-title');
            if (cardTitle) ctaName = cardTitle.textContent.trim();
        }

        const payload = {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': ctaName,
            'click_category': headingElement ? headingElement.textContent.trim() : '',
            'click_sub_category': 'card_carousel_slider'
        };
        if (index !== null) payload['index'] = index;
        
        handleCTAClick(event, payload);
        return;
    }

    // 6. Publication Showcase Card
    const publicationCta = event.target.closest('.home-report .download-cta, .engagements-insights-shaping .cta-btn');
    if (publicationCta) {
        const pubSection = publicationCta.closest('.home-report, .engagements-insights-shaping');
        const headingElement = pubSection ? pubSection.querySelector('.section-title, .insights-title') : null;
        const pubNameElement = pubSection ? pubSection.querySelector('.report-title, .insights-publication-title') : null;
        
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': publicationCta.textContent.trim() || 'download',
            'click_category': headingElement ? headingElement.textContent.trim() : '',
            'click_sub_category': 'publication_card: ' + (pubNameElement ? pubNameElement.textContent.trim() : ''),
            'index': '1'
        });
        return;
    }

    // 7. Tabbed Card Grid (Knowledge Hub, Reports)
    const tabbedGridInteraction = event.target.closest('.home-knowledge .nav-link, .home-knowledge .knowledge-card, .engagement-home-knowledge .nav-link, .engagement-home-knowledge .knowledge-card, .tab-pane .resource-card, .tab-pane .cta-btn, .home-knowledge .cta-btn');
    if (tabbedGridInteraction) {
        const gridSection = tabbedGridInteraction.closest('.home-knowledge, .engagement-home-knowledge, .nav-tabs-container, .knowledge-hub-section, .tab-content');
        const headingElement = gridSection ? gridSection.querySelector('.section-title, .knowledge-header h2') : null;
        
        const isTab = tabbedGridInteraction.classList.contains('nav-link') || tabbedGridInteraction.classList.contains('tab-btn');
        let ctaName = tabbedGridInteraction.textContent.trim() || 'click';
        let subCategory = '';
        let clickLabel = '';
        let index = null;
        
        const activeTab = gridSection ? gridSection.querySelector('.nav-link.active, .tab-btn.active') : null;
        if (activeTab) {
            subCategory = activeTab.textContent.trim();
        }

        if (isTab) {
            ctaName = 'tab: ' + ctaName;
        } else {
            const card = tabbedGridInteraction.closest('.knowledge-card, .resource-card');
            if (card) {
                const titleEl = card.querySelector('.card-title, .video-title, .resource-title');
                if (titleEl) clickLabel = titleEl.textContent.trim();
                
                const activePane = gridSection ? gridSection.querySelector('.tab-pane.active.show, .tab-pane.active') : null;
                if (activePane) {
                    const cards = Array.from(activePane.querySelectorAll('.knowledge-card, .resource-card'));
                    index = cards.indexOf(card) + 1;
                }
                
                // Determine if a specific button was clicked within the card
                if (!tabbedGridInteraction.classList.contains('knowledge-card') && !tabbedGridInteraction.classList.contains('resource-card')) {
                     ctaName = tabbedGridInteraction.textContent.trim() || tabbedGridInteraction.getAttribute('aria-label') || 'play';
                } else {
                     ctaName = 'card_click';
                }
            }
        }

        const payload = {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': ctaName,
            'click_category': 'tabbed_card_grid : ' + (headingElement ? headingElement.textContent.trim() : 'knowledge_hub')
        };

        if (subCategory && !isTab) payload['click_sub_category'] = subCategory;
        if (clickLabel) payload['click_label'] = clickLabel;
        if (index !== null) payload['index'] = index;

        handleCTAClick(event, payload);
        return;
    }

    // 8. Card List (Leadership Outputs, In the News, Media)
    const cardListInteraction = event.target.closest('.initiatives-outputs-section .article-card, .in-the-news-section .news-card, .leadership-outputs .article-card, .news-card, .media-card, .news-grid .card, .media-grid .card');
    if (cardListInteraction && !tabbedGridInteraction) { // Avoid overlap if a card is inside a tabbed grid
        const listSection = cardListInteraction.closest('.initiatives-outputs-section, .in-the-news-section, .leadership-outputs, .news-grid-section, .media-section');
        const headingElement = listSection ? listSection.querySelector('.initiatives-outputs-title, .section-title, h2') : null;
        
        const cards = Array.from(document.querySelectorAll('.article-card, .news-card, .media-card, .news-grid .card, .media-grid .card'));
        const index = cards.indexOf(cardListInteraction) + 1;
        const titleEl = cardListInteraction.querySelector('.card-title, .news-title, .media-title, .media-card-title, h3');
        const isCta = event.target.closest('.cta-btn');
        
        const clickCtaName = isCta ? event.target.textContent.trim() : (titleEl ? titleEl.textContent.trim() : 'read_more');
        
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': clickCtaName,
            'click_category': headingElement ? headingElement.textContent.trim() : 'card_list',
            'index': index
        });
        return;
    }

    // 9. Article Details - CTA & Download
    const articleCta = event.target.closest('.article-details-section .cta-btn, .article-details-section .download-btn, .article-layout-section .cta-btn, .knowledge-hub-article .cta-btn, .document-download-card .cta-btn');
    console.log("Article CTA matched:", !!articleCta);
    if (articleCta) {
        let ctaName = articleCta.textContent.trim();
        
        console.log("Calling handleCTAClick for articleCta");
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': ctaName,
            'click_category': 'article_details : ' + document.title
        });
        return;
    }

    // 9. Article CTA (Download / Social Share)
    const articleCTA = event.target.closest('.article-content .download-btn, .article-content .social-share-btn');
    if (articleCTA) {
        const articleSection = articleCTA.closest('.article-content');
        const headingElement = articleSection ? articleSection.querySelector('h1') : null;
        
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': articleCTA.textContent.trim() || articleCTA.getAttribute('aria-label') || 'share',
            'click_category': headingElement ? headingElement.textContent.trim() : ''
        });
        return;
    }

    // 10. Contact Us Tile
    const contactTile = event.target.closest('.contact-details-section .contact-tile a, .contact-card a, .address-detail a');
    if (contactTile) {
        const href = contactTile.getAttribute('href') || '';
        const type = href.startsWith('mailto:') ? 'email' : href.startsWith('tel:') ? 'phone' : 'directions';
                     
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': contactTile.textContent.trim() || type,
            'click_category': 'contact_click',
            'click_sub_category': type
        });
        return;
    }

    // 11. Social Feed
    const socialFeedCard = event.target.closest('.channels-section .channel-card, .social-feed-section .social-card');
    if (socialFeedCard) {
        const feedSection = socialFeedCard.closest('.channels-section, .social-feed-section');
        const captionEl = socialFeedCard.querySelector('.channel-card-title, .social-caption');
        
        const cards = Array.from(feedSection.querySelectorAll('.channel-card, .social-card'));
        const index = cards.indexOf(socialFeedCard) + 1;
        
        // Find if a specific share icon was clicked
        const isShare = event.target.closest('.channel-card-engagement .engagement-item');
        
        handleCTAClick(event, {
            'event': 'eventtracker',
            'custom_event_name': 'tile_interaction',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': captionEl ? captionEl.textContent.trim() : 'social_post',
            'click_category': 'social_feed',
            'click_sub_category': isShare ? 'share_click' : 'card_click',
            'index': index
        });
        return;
    }
});

// Form Submission Tracking
document.addEventListener('submit', function(event) {
    const form = event.target;
    
    // Check if it's the contact form or a form inside a recognized section
    if (form.id === 'contactForm' || form.closest('.contact-form-section, .contact-section')) {
        let formType = 'contact_us_form';
        let formCategory = 'general_inquiry';
        
        // Try to get chosen tab if inside tabs
        const tabContainer = form.closest('.contact-form-inner, .contact-section, .contact-help');
        if (tabContainer) {
            const activeTab = tabContainer.querySelector('.nav-link.active, .contact-tab.active');
            if (activeTab) {
                formCategory = activeTab.textContent.trim();
            }
        }
        
        window.dataLayer.push(transformObject({
            'event': 'eventtracker',
            'custom_event_name': 'form_submission',
            'page_url': window.location.href,
            'page_name': document.body?.getAttribute('data-body-class') || document.title,
            'click_cta_name': 'submit',
            'form_type': formType,
            'form_category': formCategory
        }));
    }
});
